import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (name, email, password) => api.post('/auth/register', { name, email, password }),
  getMe: () => api.get('/auth/me'),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getClients: (skip = 0, limit = 20) => api.get(`/admin/clients?skip=${skip}&limit=${limit}`),
  createClient: (data) => api.post('/admin/clients', data),
  updateClient: (clientId, data) => api.put(`/admin/clients/${clientId}`, data),
  deleteClient: (clientId) => api.delete(`/admin/clients/${clientId}`),
  getVendors: () => api.get('/vendors'),
  createVendor: (data) => api.post('/admin/vendors', data),
  updateVendor: (vendorCode, data) => api.put(`/admin/vendors/${vendorCode}`, data),
};

// Vendor API
export const vendorAPI = {
  list: () => api.get('/vendors'),
};

// API Key API
export const apiKeyAPI = {
  list: () => api.get('/api-keys'),
  create: (data) => api.post('/api-keys', data),
  delete: (keyId) => api.delete(`/api-keys/${keyId}`),
  test: (keyId) => api.post(`/api-keys/${keyId}/test`),
};

// Project API
export const projectAPI = {
  list: () => api.get('/projects'),
  get: (projectId) => api.get(`/projects/${projectId}`),
  create: (data) => api.post('/projects', data),
  update: (projectId, data) => api.put(`/projects/${projectId}`, data),
  delete: (projectId) => api.delete(`/projects/${projectId}`),
};

// Training Data API
export const trainingDataAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/training-data`),
  upload: (projectId, apiKeyId, name, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(
      `/projects/${projectId}/training-data/upload?api_key_id=${apiKeyId}&name=${encodeURIComponent(name)}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  delete: (dataId) => api.delete(`/training-data/${dataId}`),
};

// Job API
export const jobAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/jobs`),
  get: (jobId) => api.get(`/jobs/${jobId}`),
  getDetail: (jobId) => api.get(`/jobs/${jobId}/detail`),
  create: (projectId, data) => api.post(`/projects/${projectId}/jobs`, { project_id: projectId, ...data }),
  cancel: (jobId) => api.post(`/jobs/${jobId}/cancel`),
};

// Model API
export const modelAPI = {
  list: () => api.get('/models'),
  listByProject: (projectId) => api.get(`/projects/${projectId}/models`),
  update: (modelId, data) => api.put(`/models/${modelId}`, data),
  getCodeExamples: (modelId) => api.get(`/code-examples/${modelId}`),
};

// Playground API
export const playgroundAPI = {
  chat: (apiKeyId, modelId, messages, temperature = 0.7, maxTokens = null, useRag = false, knowledgeBaseIds = [], documentIds = []) =>
    api.post('/playground/chat', {
      api_key_id: apiKeyId,
      model_id: modelId,
      messages,
      temperature,
      max_tokens: maxTokens,
      use_rag: useRag,
      knowledge_base_ids: knowledgeBaseIds,
      document_ids: documentIds,
    }),
};

// Chat Sessions API
export const chatSessionAPI = {
  list: (limit = 20) => api.get(`/chat-sessions?limit=${limit}`),
  get: (sessionId) => api.get(`/chat-sessions/${sessionId}`),
  create: (data) => api.post('/chat-sessions', data),
  update: (sessionId, data) => api.put(`/chat-sessions/${sessionId}`, null, { params: data }),
  delete: (sessionId) => api.delete(`/chat-sessions/${sessionId}`),
  sendMessage: (sessionId, data) => api.post(`/chat-sessions/${sessionId}/messages`, data),
};

// Knowledge Base API
export const knowledgeBaseAPI = {
  list: () => api.get('/knowledge-bases'),
  get: (kbId) => api.get(`/knowledge-bases/${kbId}`),
  create: (data) => api.post('/knowledge-bases', data),
  update: (kbId, data) => api.put(`/knowledge-bases/${kbId}`, data),
  delete: (kbId) => api.delete(`/knowledge-bases/${kbId}`),

  // Document operations
  listDocuments: (kbId) => api.get(`/knowledge-bases/${kbId}/documents`),
  uploadDocument: (kbId, file, docId, options = {}, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (docId) formData.append('doc_id', docId);
    if (options.mode) formData.append('mode', options.mode);
    if (options.apiKeyId) formData.append('api_key_id', options.apiKeyId);
    if (options.modelId) formData.append('model_id', options.modelId);

    return api.post(`/knowledge-bases/${kbId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      }
    });
  },
  deleteDocument: (kbId, docId) => api.delete(`/knowledge-bases/${kbId}/documents/${docId}`),

  // Query
  query: (query, kbIds = [], topK = 5) => api.post('/knowledge-bases/query', {
    query,
    kb_ids: kbIds,
    top_k: topK
  }),
};

export default api;
